import { useState, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { btn } from "../styles/shared";
import { calcDynamicLeanMass, calcLBM } from "../utils/calculations";
import { fmtDate, fmtDay, getTodayDate } from "../utils/formatting";
import MacroBar from "../components/MacroBar";
import { MEALS } from "../constants/config";
import { apiFetch } from "../utils/apiClient";

const AUTO_URL = process.env.NODE_ENV === "development"
  ? "http://localhost:8788/api/autobiography"
  : "/api/autobiography";

const TREND_LABEL = {
  consistently_down: { text: "consistently dropping", color: "#22c55e" },
  trending_down:     { text: "trending down",          color: "#22c55e" },
  stable:            { text: "stable",                  color: "#94a3b8" },
  consistently_up:   { text: "rising",                  color: "#f87171" },
};
const COMPLIANCE_LABEL = {
  improving: { text: "improving", color: "#22c55e" },
  steady:    { text: "steady",    color: "#94a3b8" },
  declining: { text: "declining", color: "#f87171" },
};
const WORKOUT_LABEL = {
  up:     { text: "up",     color: "#22c55e" },
  steady: { text: "steady", color: "#94a3b8" },
  down:   { text: "down",   color: "#f87171" },
};

export default function Progress({
  progressTab, setProgressTab,
  weightLog,
  fitnessGoals,
  sortedLogs, getDayTotals,
  showToast,
}) {
  const [autobiography, setAutobiography] = useState(null);
  const [autoLoading,   setAutoLoading]   = useState(false);

  useEffect(() => {
    if (progressTab !== "History") return;
    if (autobiography !== null) return;
    setAutoLoading(true);
    apiFetch(AUTO_URL)
      .then(r => r.json())
      .then(data => setAutobiography(Array.isArray(data) ? data : []))
      .catch(() => setAutobiography([]))
      .finally(() => setAutoLoading(false));
  }, [progressTab]); // eslint-disable-line

  return (
<div style={{ display:"flex",flexDirection:"column",gap:20 }}>
  {/* Header */}
  <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-end",flexWrap:"wrap",gap:12 }}>
    <div>
      <h1 style={{ fontSize:26,fontWeight:600,margin:0,letterSpacing:-0.5 }}>Progress</h1>
      <p style={{ color:"#999",margin:"4px 0 0",fontSize:14 }}>Weight · Nutrition · Meal history</p>
    </div>
  </div>

  {/* Sub-tab pills */}
  <div className="nav-scroll" style={{ display:"flex",gap:6,borderBottom:"1px solid rgba(255,255,255,0.06)",paddingBottom:14,overflowX:"auto",WebkitOverflowScrolling:"touch" }}>
    {[["Weight","⚖️","#a78bfa"],["Nutrition","🍽","#fb923c"],["Meals","📋","#555"],["History","📖","#22d3ee"]].map(([t,icon,c])=>(
      <button key={t} onClick={()=>setProgressTab(t)} style={{
        ...btn, padding:"8px 14px", fontSize:12, flexShrink:0,
        background: progressTab===t ? `${c}22` : "rgba(255,255,255,0.03)",
        color:      progressTab===t ? c : "#666",
        border:     `1px solid ${progressTab===t ? c+"55" : "rgba(255,255,255,0.1)"}`,
        fontWeight: progressTab===t ? 600 : 400,
      }}>{icon} {t}</button>
    ))}
  </div>

  {/* ── WEIGHT TAB ── */}
  {progressTab==="Weight" && (()=>{
    const sorted = [...weightLog].sort((a,b)=>a.date.localeCompare(b.date));
    const chartData = sorted.map(w=>({ date:fmtDate(w.date), weight:w.weight,
      lbm: (() => {
        const value = calcDynamicLeanMass(w.weight, fitnessGoals, weightLog) ?? calcLBM(w.weight, fitnessGoals.height);
        return value != null ? parseFloat(value.toFixed(1)) : null;
      })(),
      target: fitnessGoals.weightTarget||72 }));
    const latest = sorted[sorted.length-1];
    const first  = sorted[0];
    const totalLost = first && latest ? (first.weight - latest.weight).toFixed(2) : null;
    const toGo = latest ? (latest.weight - (fitnessGoals.weightTarget||72)).toFixed(1) : null;
    const streak = (()=>{
      let s=0; const today=getTodayDate();
      const byDate = Object.fromEntries(sorted.map(w=>[w.date,w]));
      let d = new Date(today);
      while(byDate[d.toISOString().split("T")[0]]) { s++; d.setDate(d.getDate()-1); }
      return s;
    })();
    return (
      <div style={{ display:"flex",flexDirection:"column",gap:16 }}>
        {/* Stat cards */}
        <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:10 }}>
          {[
            ["Current Weight",  latest?latest.weight+"kg":"—"],
            ["Target Weight",   (fitnessGoals.weightTarget||72)+"kg"],
            ["Still to Lose",   toGo>0?toGo+"kg":"🎯 Done!"],
            ["Total Lost",      totalLost>0?"↓"+totalLost+"kg":"—"],
            ["Logging Streak",  streak+"d in a row"],
            ["Total Entries",   sorted.length+" weigh-ins"],
          ].map(([l,v])=>(
            <div key={l} style={{ background:"linear-gradient(135deg,#0f0b1e,#0a0817)",border:"1px solid rgba(139,92,246,0.15)",borderRadius:12,padding:"13px 15px" }}>
              <div style={{ fontSize:10,fontFamily:"'Space Mono',monospace",color:"#b8a8d0",textTransform:"uppercase",letterSpacing:1,marginBottom:6 }}>{l}</div>
              <div style={{ fontSize:18,fontWeight:700,fontFamily:"'Space Mono',monospace",color:"#fff" }}>{v}</div>
            </div>
          ))}
        </div>
        {/* Weight chart */}
        {chartData.length>1 ? (
          <div style={{ background:"linear-gradient(135deg,#0f0b1e,#0a0817)",border:"1px solid rgba(139,92,246,0.15)",borderRadius:16,padding:20 }}>
            <div style={{ fontSize:12,fontFamily:"'Space Mono',monospace",color:"#c4b4d8",marginBottom:14,textTransform:"uppercase",letterSpacing:1 }}>Weight over time</div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(139,92,246,0.07)"/>
                <XAxis dataKey="date" tick={{fill:"#b8a8d0",fontSize:10}} axisLine={false} tickLine={false}/>
                <YAxis domain={["auto","auto"]} tick={{fill:"#b8a8d0",fontSize:10}} axisLine={false} tickLine={false} width={40}/>
                <Tooltip contentStyle={{background:"#0f0b1e",border:"1px solid rgba(139,92,246,0.3)",borderRadius:10,color:"#e2e8f0"}} labelStyle={{color:"#a78bfa",fontSize:11}}/>
                <Line type="monotone" dataKey="weight" stroke="#a78bfa" strokeWidth={2.5} dot={{r:4,fill:"#a78bfa",strokeWidth:0}} activeDot={{r:6}} name="Weight (kg)"/>
                <Line type="monotone" dataKey="target" stroke="rgba(139,92,246,0.3)" strokeWidth={1.5} strokeDasharray="4 4" dot={false} name="Target"/>
                <Line type="monotone" dataKey="lbm" stroke="#a090c0" strokeWidth={1.5} strokeDasharray="2 2" dot={false} name="LBM (kg)"/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div style={{ background:"linear-gradient(135deg,#0f0b1e,#0a0817)",border:"1px solid rgba(139,92,246,0.15)",borderRadius:16,padding:40,textAlign:"center",color:"#b8a8d0",fontSize:13 }}>
            Log at least 2 weigh-ins to see your trend
          </div>
        )}
        {sorted.some(w => w.photoUrl) && (
          <div style={{ background:"linear-gradient(135deg,#0f0b1e,#0a0817)",border:"1px solid rgba(139,92,246,0.15)",borderRadius:16,padding:16 }}>
            <div style={{ fontSize:11,fontFamily:"'Space Mono',monospace",color:"#c4b4d8",textTransform:"uppercase",letterSpacing:1,marginBottom:12 }}>Weigh-in Photos</div>
            <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))",gap:10 }}>
              {[...sorted].reverse().filter(w => w.photoUrl).map(w => (
                <div key={w.date} style={{ background:"rgba(255,255,255,0.03)",borderRadius:10,overflow:"hidden",border:"1px solid rgba(139,92,246,0.12)" }}>
                  <img src={w.photoUrl} alt={`weigh-in ${w.date}`} style={{ width:"100%",aspectRatio:"1/1",objectFit:"cover",display:"block" }}/>
                  <div style={{ padding:"7px 8px",fontSize:10,fontFamily:"'Space Mono',monospace",color:"#b8a8d0" }}>{w.date}<br/><span style={{ color:"#a78bfa" }}>{w.weight} kg</span></div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Log table */}
        {sorted.length>0 && (
          <div style={{ background:"linear-gradient(135deg,#0f0b1e,#0a0817)",border:"1px solid rgba(139,92,246,0.15)",borderRadius:16,overflow:"hidden" }}>
            <div style={{ padding:"12px 20px",borderBottom:"1px solid rgba(139,92,246,0.1)",fontSize:11,fontFamily:"'Space Mono',monospace",color:"#c4b4d8",textTransform:"uppercase",letterSpacing:1 }}>Full log</div>
            <div style={{ maxHeight:300,overflowY:"auto" }}>
              {[...sorted].reverse().map((w,i,arr)=>{
                const prev = arr[i+1];
                const d = prev ? (w.weight-prev.weight).toFixed(2) : null;
                return (
                  <div key={w.date} style={{ padding:"10px 20px",borderBottom:"1px solid rgba(139,92,246,0.06)",display:"flex",justifyContent:"space-between",alignItems:"center" }}>
                    {w.photoUrl && <img src={w.photoUrl} alt="" style={{ width:34,height:34,borderRadius:6,objectFit:"cover",marginRight:10,border:"1px solid rgba(139,92,246,0.2)" }}/>}
                    <span style={{ fontSize:12,fontFamily:"'Space Mono',monospace",color:"#a090c0" }}>{w.date}</span>
                    <span style={{ fontSize:14,fontWeight:700,fontFamily:"'Space Mono',monospace",color:"#a78bfa" }}>{w.weight} kg</span>
                    <span style={{ fontSize:11,fontFamily:"'Space Mono',monospace",color: d===null?"#b8a8d0":parseFloat(d)<0?"#4ade80":parseFloat(d)>0?"#f87171":"#555",minWidth:60,textAlign:"right" }}>
                      {d===null?"—":parseFloat(d)>0?"+"+d+" kg":d+" kg"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  })()}

  {/* ── NUTRITION TAB ── */}
  {progressTab==="Nutrition" && (()=>{
    const nutritionData = sortedLogs.map(l=>{ const t=getDayTotals(l.date); return t?{date:fmtDate(l.date),...t}:null; }).filter(Boolean);
    const avg = key => nutritionData.length ? Math.round(nutritionData.reduce((s,d)=>s+(d[key]||0),0)/nutritionData.length) : 0;
    return (
      <div style={{ display:"flex",flexDirection:"column",gap:16 }}>
        {/* Avg stat cards */}
        <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:10 }}>
          {[["Avg Calories",avg("calories")+"kcal"],["Avg Protein",avg("protein")+"g"],["Avg Carbs",avg("carbs")+"g"],["Avg Fat",avg("fat")+"g"],["Avg Fiber",avg("fiber")+"g"],["Days Logged",sortedLogs.length+" days"]].map(([l,v])=>(
            <div key={l} style={{ background:"linear-gradient(135deg,#160c02,#110900)",border:"1px solid rgba(251,146,60,0.15)",borderRadius:12,padding:"13px 15px" }}>
              <div style={{ fontSize:10,fontFamily:"'Space Mono',monospace",color:"#ffb87a",textTransform:"uppercase",letterSpacing:1,marginBottom:6 }}>{l}</div>
              <div style={{ fontSize:18,fontWeight:700,fontFamily:"'Space Mono',monospace",color:"#fff" }}>{v}</div>
            </div>
          ))}
        </div>
        {nutritionData.length>1 ? (<>
          {/* Calories chart */}
          <div style={{ background:"linear-gradient(135deg,#160c02,#110900)",border:"1px solid rgba(251,146,60,0.15)",borderRadius:16,padding:20 }}>
            <div style={{ fontSize:12,fontFamily:"'Space Mono',monospace",color:"#ffb87a",marginBottom:14,textTransform:"uppercase",letterSpacing:1 }}>Calories over time</div>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={nutritionData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(251,146,60,0.06)"/>
                <XAxis dataKey="date" tick={{fill:"#ffb87a",fontSize:10}} axisLine={false} tickLine={false}/>
                <YAxis tick={{fill:"#ffb87a",fontSize:10}} axisLine={false} tickLine={false} width={40}/>
                <Tooltip contentStyle={{background:"#160c02",border:"1px solid rgba(251,146,60,0.3)",borderRadius:10,color:"#e2e8f0"}} labelStyle={{color:"#fb923c",fontSize:11}}/>
                <Line type="monotone" dataKey="calories" stroke="#fb923c" strokeWidth={2.5} dot={{r:3,fill:"#fb923c",strokeWidth:0}} activeDot={{r:5}} name="Calories"/>
              </LineChart>
            </ResponsiveContainer>
          </div>
          {/* Macros chart */}
          <div style={{ background:"linear-gradient(135deg,#160c02,#110900)",border:"1px solid rgba(251,146,60,0.15)",borderRadius:16,padding:20 }}>
            <div style={{ fontSize:12,fontFamily:"'Space Mono',monospace",color:"#ffb87a",marginBottom:14,textTransform:"uppercase",letterSpacing:1 }}>Macros over time</div>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={nutritionData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(251,146,60,0.06)"/>
                <XAxis dataKey="date" tick={{fill:"#ffb87a",fontSize:10}} axisLine={false} tickLine={false}/>
                <YAxis tick={{fill:"#ffb87a",fontSize:10}} axisLine={false} tickLine={false} width={40}/>
                <Tooltip contentStyle={{background:"#160c02",border:"1px solid rgba(251,146,60,0.3)",borderRadius:10,color:"#e2e8f0"}} labelStyle={{color:"#fb923c",fontSize:11}}/>
                <Line type="monotone" dataKey="protein" stroke="#60a5fa" strokeWidth={2} dot={false} name="Protein (g)"/>
                <Line type="monotone" dataKey="carbs"   stroke="#4ade80" strokeWidth={2} dot={false} name="Carbs (g)"/>
                <Line type="monotone" dataKey="fat"     stroke="#fbbf24" strokeWidth={2} dot={false} name="Fat (g)"/>
                <Line type="monotone" dataKey="fiber"   stroke="#c084fc" strokeWidth={2} dot={false} name="Fiber (g)"/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>) : (
          <div style={{ background:"linear-gradient(135deg,#160c02,#110900)",border:"1px solid rgba(251,146,60,0.15)",borderRadius:16,padding:40,textAlign:"center",color:"#ffb87a",fontSize:13 }}>Log meals on multiple days to see your nutrition trend</div>
        )}
      </div>
    );
  })()}


  {/* ── MEALS LOG TAB ── */}
  {progressTab==="Meals" && (
    <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
      {sortedLogs.length===0 ? (
        <div style={{ color:"#888",textAlign:"center",padding:56,fontSize:14 }}>No entries yet.</div>
      ) : (
        [...sortedLogs].reverse().map(log=>{
          const totals = getDayTotals(log.date);
          return (
            <div key={log.id} style={{ background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:13,padding:"15px 18px" }}>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10,marginBottom:10 }}>
                <span style={{ fontFamily:"'Space Mono',monospace",fontSize:13,color:"#f97316" }}>{fmtDate(log.date)} · {fmtDay(log.date)}</span>
                {totals && <MacroBar macros={totals} compact/>}
              </div>
              <div style={{ display:"flex",flexWrap:"wrap",gap:7 }}>
                {MEALS.map(meal=>{
                  const m = log.meals?.[meal]; if (!m) return null;
                  return (
                    <div key={meal} style={{ background:"rgba(255,255,255,0.04)",borderRadius:8,padding:"6px 12px",fontSize:12 }}>
                      <span style={{ color:"#aaa",marginRight:5 }}>{meal}:</span>
                      <span style={{ color:"#bbb" }}>{m.ingredients.map(i=>i.name).filter(Boolean).join(", ")||"—"}</span>
                      {m.macros?.calories>0 && <span style={{ color:"#f97316",marginLeft:7 }}>{m.macros.calories}kcal</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}
    </div>
  )}

  {/* ── HISTORY TAB ── */}
  {progressTab==="History" && (()=>{
    if (autoLoading) return <div style={{ textAlign:"center",padding:"48px 0",color:"#555",fontSize:13 }}>Loading autobiography…</div>;
    if (!autobiography || autobiography.length === 0) return (
      <div style={{ textAlign:"center",padding:"48px 20px",color:"#475569",fontSize:14,lineHeight:1.7 }}>
        <div style={{ fontSize:32,marginBottom:12 }}>📖</div>
        <div style={{ fontWeight:600,color:"#64748b",marginBottom:6 }}>No autobiography yet</div>
        <div>Your data portrait builds automatically each day a backup runs.<br/>Come back after the first backup completes.</div>
      </div>
    );

    const sorted = [...autobiography].sort((a,b) => b.date.localeCompare(a.date));
    const chartData = [...autobiography].sort((a,b) => a.date.localeCompare(b.date)).map(p => ({
      date:    p.date.slice(5), // MM-DD
      weight:  p.weight || null,
      lbm:     p.lbm    || null,
      protein: p.proteinAvg || null,
      compliance: p.macroCompliance != null ? Math.round(p.macroCompliance * 100) : null,
    }));

    // Latest signature data (from most recent snapshot)
    const latest = sorted[0];
    const cardStyle = { background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:14,padding:"16px 20px" };

    return (
      <div style={{ display:"flex",flexDirection:"column",gap:20 }}>

        {/* Visual journey strip — photos from each snapshot */}
        {sorted.some(p => p.photo) && (
          <div style={{ background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:14,padding:"14px 16px" }}>
            <div style={{ fontSize:11,color:"#94a3b8",fontFamily:"'Space Mono',monospace",letterSpacing:1,marginBottom:12 }}>VISUAL JOURNEY</div>
            <div style={{ display:"flex",gap:8,overflowX:"auto",paddingBottom:4 }}>
              {[...sorted].reverse().filter(p => p.photo).map(p => (
                <div key={p.date} style={{ flexShrink:0,textAlign:"center" }}>
                  <img src={p.photo} alt={p.date}
                    style={{ width:64,height:64,borderRadius:8,objectFit:"cover",border:"1px solid rgba(255,255,255,0.1)",display:"block",marginBottom:4 }}/>
                  <div style={{ fontSize:9,color:"#475569",fontFamily:"'Space Mono',monospace" }}>{p.date.slice(5)}</div>
                  <div style={{ fontSize:9,color:"#64748b" }}>{p.weight}kg</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Identity panel — only shows once enough data exists */}
        {(latest.signatureFoods?.length > 0 || latest.signatureWorkouts?.length > 0 || latest.calorieRange) && (
          <div style={{ ...cardStyle, borderLeft:"3px solid #22d3ee", borderRadius:"0 14px 14px 0" }}>
            <div style={{ fontSize:11,color:"#22d3ee",fontFamily:"'Space Mono',monospace",letterSpacing:1,marginBottom:12 }}>DATA IDENTITY</div>
            <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
              {latest.signatureFoods?.length > 0 && (
                <div style={{ fontSize:13,color:"#cbd5e1" }}>
                  <span style={{ color:"#94a3b8",marginRight:8 }}>Signature diet:</span>
                  {latest.signatureFoods.join("  ·  ")}
                </div>
              )}
              {latest.signatureWorkouts?.length > 0 && (
                <div style={{ fontSize:13,color:"#cbd5e1" }}>
                  <span style={{ color:"#94a3b8",marginRight:8 }}>Signature training:</span>
                  {latest.signatureWorkouts.join("  ·  ")}
                </div>
              )}
              {latest.calorieRange && (
                <div style={{ fontSize:13,color:"#cbd5e1" }}>
                  <span style={{ color:"#94a3b8",marginRight:8 }}>Natural calorie range:</span>
                  {latest.calorieRange.low}–{latest.calorieRange.high} kcal/day
                </div>
              )}
            </div>
          </div>
        )}

        {/* Long-range weight chart */}
        {chartData.length > 1 && (
          <div style={cardStyle}>
            <div style={{ fontSize:11,color:"#94a3b8",fontFamily:"'Space Mono',monospace",letterSpacing:1,marginBottom:14 }}>WEIGHT OVER TIME (ALL SNAPSHOTS)</div>
            <ResponsiveContainer width="100%" height={160}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)"/>
                <XAxis dataKey="date" tick={{fill:"#64748b",fontSize:10}} axisLine={false} tickLine={false}/>
                <YAxis tick={{fill:"#64748b",fontSize:10}} axisLine={false} tickLine={false} width={42} domain={["auto","auto"]}/>
                <Tooltip contentStyle={{background:"#0f0f1a",border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,color:"#e2e8f0",fontSize:12}} labelStyle={{color:"#22d3ee"}}/>
                <Line type="monotone" dataKey="weight" stroke="#a78bfa" strokeWidth={2.5} dot={{r:4,fill:"#a78bfa",strokeWidth:0}} name="Weight (kg)" connectNulls/>
                <Line type="monotone" dataKey="lbm"    stroke="rgba(167,139,250,0.35)" strokeWidth={1.5} dot={false} name="LBM (kg)" connectNulls strokeDasharray="4 3"/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Compliance + workout chart */}
        {chartData.length > 1 && (
          <div style={cardStyle}>
            <div style={{ fontSize:11,color:"#94a3b8",fontFamily:"'Space Mono',monospace",letterSpacing:1,marginBottom:14 }}>PROTEIN COMPLIANCE % · WORKOUTS/WK</div>
            <ResponsiveContainer width="100%" height={140}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)"/>
                <XAxis dataKey="date" tick={{fill:"#64748b",fontSize:10}} axisLine={false} tickLine={false}/>
                <YAxis tick={{fill:"#64748b",fontSize:10}} axisLine={false} tickLine={false} width={42}/>
                <Tooltip contentStyle={{background:"#0f0f1a",border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,color:"#e2e8f0",fontSize:12}} labelStyle={{color:"#22d3ee"}}/>
                <Line type="monotone" dataKey="compliance" stroke="#f97316" strokeWidth={2} dot={{r:3,fill:"#f97316",strokeWidth:0}} name="Compliance %" connectNulls/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Portrait cards — chronological, most recent first */}
        {sorted.map((p, idx) => {
          const wt  = TREND_LABEL[p.weightTrend];
          const ct  = COMPLIANCE_LABEL[p.complianceTrend];
          const wkt = WORKOUT_LABEL[p.workoutTrend];
          return (
            <div key={p.date} style={cardStyle}>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:8,marginBottom:14 }}>
                <div>
                  <div style={{ fontSize:13,fontFamily:"'Space Mono',monospace",color:"#22d3ee",marginBottom:2 }}>{p.date}</div>
                  <div style={{ fontSize:11,color:"#475569" }}>Snapshot #{p.snapshotCount ?? (autobiography.length - idx)}</div>
                </div>
                <div style={{ display:"flex",gap:10,flexWrap:"wrap" }}>
                  {wt  && <span style={{ fontSize:10,fontFamily:"'Space Mono',monospace",color:wt.color,background:"rgba(0,0,0,0.25)",padding:"2px 8px",borderRadius:4 }}>weight {wt.text}</span>}
                  {ct  && <span style={{ fontSize:10,fontFamily:"'Space Mono',monospace",color:ct.color,background:"rgba(0,0,0,0.25)",padding:"2px 8px",borderRadius:4 }}>compliance {ct.text}</span>}
                  {wkt && <span style={{ fontSize:10,fontFamily:"'Space Mono',monospace",color:wkt.color,background:"rgba(0,0,0,0.25)",padding:"2px 8px",borderRadius:4 }}>workouts {wkt.text}</span>}
                </div>
              </div>

              {/* Weigh-in photo */}
              {p.photo && (
                <img src={p.photo} alt={`weigh-in ${p.date}`}
                  style={{ width:"100%",maxWidth:180,borderRadius:10,objectFit:"cover",marginBottom:14,border:"1px solid rgba(255,255,255,0.08)",display:"block" }}/>
              )}

              {/* Stat grid */}
              <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))",gap:8,marginBottom:p.insights?.length ? 14 : 0 }}>
                {[
                  ["Weight",    p.weight ? p.weight+"kg"+(p.weightDelta!=null ? ` (${p.weightDelta>0?"+":""}${p.weightDelta})`:"") : "—", "#a78bfa"],
                  ["Body Fat",  p.bodyFat != null ? p.bodyFat+"%" : "—", "#f97316"],
                  ["LBM",       p.lbm != null ? p.lbm+"kg" : "—", "#a78bfa"],
                  ["Avg Cal",   p.calorieAvg ? p.calorieAvg+"kcal" : "—", "#fb923c"],
                  ["Avg Protein", p.proteinAvg ? p.proteinAvg+"g" : "—", "#f97316"],
                  ["Compliance", p.macroCompliance != null ? Math.round(p.macroCompliance*100)+"%" : "—", "#22c55e"],
                  ["Workouts/wk", p.workoutsPerWeek != null ? p.workoutsPerWeek : "—", "#34d399"],
                  ["Active days", p.activeDays != null ? p.activeDays+"/30" : "—", "#94a3b8"],
                ].map(([l,v,c]) => (
                  <div key={l} style={{ background:"rgba(255,255,255,0.03)",borderRadius:8,padding:"8px 10px" }}>
                    <div style={{ fontSize:9,fontFamily:"'Space Mono',monospace",color:"#475569",textTransform:"uppercase",letterSpacing:0.8,marginBottom:4 }}>{l}</div>
                    <div style={{ fontSize:14,fontWeight:700,fontFamily:"'Space Mono',monospace",color:c }}>{v}</div>
                  </div>
                ))}
              </div>

              {/* Top foods */}
              {p.topFoods?.length > 0 && (
                <div style={{ display:"flex",gap:6,flexWrap:"wrap",marginTop:10,marginBottom:p.insights?.length ? 10 : 0 }}>
                  {p.topFoods.map(f => (
                    <span key={f} style={{ fontSize:10,color:"#64748b",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:5,padding:"2px 8px" }}>{f}</span>
                  ))}
                </div>
              )}

              {/* Insights */}
              {p.insights?.length > 0 && (
                <div style={{ display:"flex",flexDirection:"column",gap:5,marginTop:10,paddingTop:10,borderTop:"1px solid rgba(255,255,255,0.06)" }}>
                  {p.insights.map((ins,i) => (
                    <div key={i} style={{ fontSize:12,color:"#94a3b8",lineHeight:1.5 }}>→ {ins}</div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  })()}

</div>
  );
}
